import { Button, MenuItem, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useEffect, useRef, useState } from "react";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

type AnalyticsProps = {
    pageName: string;
    buttonType: ButtonClickAnalyticsType;
    id?: string;
};

export interface DeletePlaylistMenuItemProps extends SharedMenuItemProps {
    deleteUrl: string;
    confirmationTitle: string;
    confirmationMessage: string;
    confirmationButtonText: string;
    playlistName: string;
    playlistId: string;
    redirectUrl?: string;
    analytics?: AnalyticsProps;
}

/**
 * playlist menu item for deleting the playlist.
 */
export const DeletePlaylistMenuItem = (props: DeletePlaylistMenuItemProps) => {
    const {
        deleteUrl,
        confirmationTitle,
        confirmationMessage,
        confirmationButtonText,
        playlistName,
        playlistId,
        onCloseMenu,
        reloadMedia,
        redirectUrl,
        analytics,
        ...menuItemProps
    } = props;
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);
    const shouldReload = useRef(false);

    const closeConfirmation = () => {
        setOpenConfirmation(false);
        onCloseMenu?.();
    };

    // Run reloadMedia after this component unmounts, if needed
    useEffect(() => {
        return () => {
            if (shouldReload.current) {
                reloadMedia?.();
            }
        };
    }, [reloadMedia]);

    const { showToast } = useToastsContext();

    const sendButtonAnalytics = useButtonAnalytics();

    const handleDelete = async () => {
        setProcessing(true);
        sendButtonAnalytics(
            "Confirm Delete",
            ButtonClickAnalyticsType.DELETE,
            playlistId,
            "my-playlists",
            "Modal - Delete playlist"
        );
        try {
            const result = await postKmsData(deleteUrl);
            if (result.success) {
                showToast({
                    severity: "success",
                    message: translate('Playlist "%1" successfully deleted.', [playlistName]),
                });
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                else {
                    // mark for reload once this modal is closed/unmounted
                    shouldReload.current = true;
                }
            }
            else {
                showToast({
                    severity: "error",
                    message: translate('Failed to delete the playlist "%1"', [playlistName]),
                });
            }
            setProcessing(false);
            closeConfirmation();
        }
        catch (e: unknown) {
            closeConfirmation();
            setProcessing(false);
            showToast({ severity: "error", message: translate('Failed to delete the playlist "%1"', [playlistName]) });
        }
    };

    const onMenuButtonClick = () => {
        setOpenConfirmation(true);

        if (analytics?.pageName && analytics?.buttonType) {
            sendButtonAnalytics(analytics.pageName, analytics.buttonType, analytics.id);
        }
        else {
            sendButtonAnalytics("Delete Playlist", ButtonClickAnalyticsType.DELETE, playlistId);
        }
    };

    return (
        <>
            <MenuItem {...menuItemProps} onClick={onMenuButtonClick}>
                {translate("Delete")}
            </MenuItem>

            <Modal
                open={openConfirmation}
                onClose={closeConfirmation}
                maxWidth={false}
                onKeyDown={(ev) => ev.stopPropagation()}
            >
                <ModalTitle>{confirmationTitle}</ModalTitle>
                <ModalContent>{HtmlReactParser(confirmationMessage)}</ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={closeConfirmation}>
                        {translate("Cancel")}
                    </Button>
                    <Button color="danger" onClick={handleDelete} loading={processing} disabled={processing}>
                        {confirmationButtonText}
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};
